/**
 * useSystemStatus — fetches execution mode from GET /api/v1/system/status
 *
 * Provides:
 *   executionMode: "simulation" | "paper" | "live"
 *   isSimulation, isPaper, isLive
 *   safetyLock: true if live mode is blocked
 *   loading, error
 *
 * PHASE 5: Frontend mode awareness.
 */

import { useState, useEffect, useRef } from 'react';

import { apiV1Base } from '../lib/apiConfig';
interface SystemStatus {
  executionMode: 'simulation' | 'paper' | 'live';
  liveEnabled: boolean;
  liveAllowed: boolean;
  safetyLock: boolean;
}

interface UseSystemStatus extends SystemStatus {
  isSimulation: boolean;
  isPaper: boolean;
  isLive: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function getEnv(key: string, fallback: string): string {
  try {
    return (import.meta as any).env?.[key] ?? fallback;
  } catch {
    return fallback;
  }
}

const GATEWAY_BASE = apiV1Base();
const API_KEY = getEnv('VITE_API_KEY', 'adversyn-dev-key-2026');

const DEFAULT_STATUS: SystemStatus = {
  executionMode: 'simulation',
  liveEnabled: false,
  liveAllowed: false,
  safetyLock: false,
};

export function useSystemStatus(): UseSystemStatus {
  const [status, setStatus] = useState<SystemStatus>(DEFAULT_STATUS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${GATEWAY_BASE}/system/status`, {
        headers: { 'x-api-key': API_KEY },
        signal: controller.signal,
      });

      clearTimeout(timer);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const json = await response.json();

      if (mountedRef.current && json.success && json.data) {
        setStatus({
          executionMode: json.data.execution_mode ?? 'simulation',
          liveEnabled: json.data.live_enabled ?? false,
          liveAllowed: json.data.live_allowed ?? false,
          safetyLock: json.data.safety_lock ?? false,
        });
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch status');
        // Default to simulation on error — safest assumption
        setStatus(DEFAULT_STATUS);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchStatus();
    return () => { mountedRef.current = false; };
  }, []);

  return {
    ...status,
    isSimulation: status.executionMode === 'simulation',
    isPaper: status.executionMode === 'paper',
    isLive: status.executionMode === 'live',
    loading,
    error,
    refetch: fetchStatus,
  };
}
